import React, { useState, useMemo } from 'react';
import './ModernMinimalist.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const ModernMinimalist = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="mm-root">
            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-modern-minimalist.pdf` })} />

            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <div className="mm-menu-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <div
                            key={`${page.id || page.name}-${index}`}
                            className="mm-card pt-page active"
                        >
                            {/* Restaurant header */}
                            {showHeader && (
                                <header className="mm-header">
                                    <h1>{restaurant.name}</h1>
                                    <div className="mm-meta">{restaurant.address || ''} {restaurant.phoneNumber ? ' • ' + restaurant.phoneNumber : ''}</div>
                                </header>
                            )}

                            {/* Menu content */}
                            <div className="mm-body">
                                {showMenuTitle && (
                                    <h2 className="mm-menu-title">{page.name}</h2>
                                )}
                                {categories.map(category => (
                                    <div key={category.id} className="mm-cat">
                                        <h3 className="mm-cat-title">{category.name}</h3>
                                        <div className="mm-cat-items">
                                            {(category.items || []).map(item => (
                                                <div key={item.id} className="mm-item">
                                                    <div className="mm-item-left">
                                                        <div className="mm-item-name-row">
                                                            {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                <span className={`mm-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                    <span className="mm-veg-mark"></span>
                                                                </span>
                                                            )}
                                                            <div className="mm-item-name">{item.name}</div>
                                                        </div>
                                                        {item.description && <div className="mm-item-desc">{item.description}</div>}
                                                    </div>
                                                    <div className="mm-item-price">{item.price ? `₹${item.price.toFixed(2)}` : ''}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ModernMinimalist;
